import * as z from 'zod'

import { POSSIBLE_FIN_CATEGORY_KEYS } from '#dictionary'

// Which parsed field of a matched record a quest may override.
//
// `annualValue` is the figure the timeline projects from, so overriding it means
// "this record is worth X a year" and leaves the growth curve to the category.
// `dateRange` is the record's own window, in whole ages.
//
// Enumerated rather than a free string so adding a field is a deliberate schema
// change: each one needs a stored shape decided for it (a guard test in
// quests/schemas.test.ts fails until it has one) and, usually, its own control.
//
// Declared here, on the quest's own tuning, rather than on the target topic's
// `finRecordBuilder`: what a quest may change is a property of the quest, not of
// the plan record it points at. That is what lets the survey YAML stay free of
// quest concerns (ZL-815).
export const recordFieldSchema = z.enum(['annualValue', 'dateRange'])

export type RecordField = z.output<typeof recordFieldSchema>

// Fallback label per axis, used when a tuning does not name one. Spells out the
// period deliberately: the parsed value is annual while the plan may have
// captured it monthly, so a slider labelled just "amount" next to a figure twelve
// times the one the user typed is a trap.
const DEFAULT_FIELD_LABELS: Record<RecordField, string> = {
  annualValue: 'Yearly amount',
  dateRange: 'When it runs',
}

// Which pole of a date range a quest may move. `both` gives a true double slider;
// `start` or `end` freezes the other thumb, which still renders so the range stays
// readable.
export const dateRangeDirectionSchema = z.enum(['start', 'end', 'both'])

export type DateRangeDirection = z.output<typeof dateRangeDirectionSchema>

// An axis is an object rather than a bare field name so the label sits with the
// axis it describes, and because the axis is where the next per-field settings
// belong — `betterWhen` (lower is better for a rent end date, higher for a start
// date) and the bounds a slider needs. A parallel `fieldLabels` map keyed by field
// would duplicate this key space and then have to be kept in step with it.
//
// A bound is what a money axis may declare. Relatives exist because one global
// range cannot suit both a $24k rent and a $1.2k subscription.
//
//   30000     absolute money
//   '2x'      a multiple of the record's own baseline
//   '75%'     a percentage of the baseline; '300%' is the same as '3x'
//   '+2000'   an offset from the baseline (sign required, or it reads as money)
const absoluteBoundSchema = z.number().min(0)
const multipleBoundSchema = z.templateLiteral([z.number().min(0), 'x'])
const percentBoundSchema = z.templateLiteral([z.number(), '%'])
const offsetBoundSchema = z.string().regex(/^[+-]\d+(?:\.\d+)?$/, {
  error:
    'a relative bound must be "2x", "75%", or a signed offset like "+2000"',
})

const boundSchema = z.union([
  absoluteBoundSchema,
  multipleBoundSchema,
  percentBoundSchema,
  offsetBoundSchema,
])

// `range` is relative only — an absolute number says nothing about which
// direction to go — and its sign is ignored, since a range goes both ways however
// it is written.
//
// Note `%` reads differently here than in min/max. As a bound it is a LEVEL
// (`max: '100%'` is the baseline itself); as a range it is a SPREAD
// (`range: '100%'` is room to move by a whole baseline each way). Each is the
// natural reading for its position.
//
const rangeSchema = z
  .union([multipleBoundSchema, percentBoundSchema, offsetBoundSchema])
  // A multiplier of zero is the one relative form a range cannot take: it
  // resolves to [baseline / n, baseline * n], so '0x' divides by zero and leaves
  // the slider with no lower bound. Zero stays valid as a min or a max, where it
  // is a level rather than a factor.
  //
  // Refined on the union rather than on the multiple member, so the message
  // reaches the author — a member's own error is reported as a bare "Invalid
  // input" once the union aggregates it.
  .refine(value => !/^0+(?:\.0+)?x$/.test(String(value)), {
    error: 'a range of "0x" would divide the baseline by zero',
  })

// What the row and the slider call this axis. Defaults per field, so a template
// only writes it when the generic wording is wrong for the record — "Yearly rent"
// reads better than "Yearly amount" on a rent slot.
const axisLabelSchema = z.string().trim().min(1).optional()

export const moneyAxisSchema = z
  .strictObject({
    field: z.literal('annualValue'),
    label: axisLabelSchema,
    // How far the quest may move this axis. Omitted means zero to the money
    // ceiling. `range` sets both sides at once; declaring it alongside `min` or
    // `max` is a config error rather than a precedence puzzle.
    min: boundSchema.optional(),
    max: boundSchema.optional(),
    range: rangeSchema.optional(),
  })
  .refine(
    axis =>
      axis.range === undefined ||
      (axis.min === undefined && axis.max === undefined),
    { error: 'an axis declares either range, or min/max — not both' }
  )
  .transform(entry => ({
    ...entry,
    label: entry.label ?? DEFAULT_FIELD_LABELS.annualValue,
  }))

// No bounds here on purpose: a date range runs from today to life expectancy,
// resolved per owner, and a money bound like '2x' is meaningless on an age.
// Declaring one is an unrecognised key rather than a value that gets ignored.
//
// TODO(ZL-815): once the bounds can be dynamic ("until retirement", "when the
// youngest turns 18"), a dateRange axis will want to name them here.
export const dateRangeAxisSchema = z
  .strictObject({
    field: z.literal('dateRange'),
    label: axisLabelSchema,
    // Required, not optional-with-a-refine. Which pole moves is the whole point
    // of the axis, and making it structural is what lets a reader use
    // `axis.direction` without a cast.
    direction: dateRangeDirectionSchema,
  })
  .transform(entry => ({
    ...entry,
    label: entry.label ?? DEFAULT_FIELD_LABELS.dateRange,
  }))

/**
 * One axis a tuning slot may adjust.
 *
 * A discriminated union rather than one object with optional fields, because the
 * two axes take genuinely different settings: money takes bounds, a date range
 * takes a direction. Three cross-field refines used to enforce that by hand
 * ("direction applies only to a dateRange axis", and so on); the union enforces
 * all three structurally, reports them at a more precise path, and — the reason
 * that matters — makes `direction` a required field on the variant that has it,
 * so the builder reads it without asserting.
 */
export const tuningAxisSchema = z.discriminatedUnion(
  'field',
  [moneyAxisSchema, dateRangeAxisSchema],
  // Named explicitly because an unmatched discriminator otherwise reports a bare
  // "Invalid input", and a misspelled field is the likeliest authoring mistake.
  { error: 'an axis field must be one of "annualValue", "dateRange"' }
)

export type MoneyAxis = z.output<typeof moneyAxisSchema>
export type DateRangeAxis = z.output<typeof dateRangeAxisSchema>
export type TuningAxis = z.output<typeof tuningAxisSchema>

const tuningSchema = z
  .strictObject({
    title: z.string(),
    info: z.string().nullish(),
    emptyStateInfo: z.string().nullish(),
    icon: z.string().nullish(),
    isOptional: z.boolean().default(false),
    // Any submission whose ROOT record resolves to one of these categories is a
    // candidate. No opt-in is needed on the target topic — matching the category is
    // the whole rule.
    filterCategoryKeys: z.array(z.enum(POSSIBLE_FIN_CATEGORY_KEYS)).min(1),
    // The record axes this slot may adjust. NOT defaulted: omitting it means the
    // slot adjusts nothing, the same way `canMute` omitted means it may not turn a
    // record off. A slot that only mutes is a real case — "buying stops the rent"
    // needs no slider — and defaulting this would hand every tuning a value slider
    // it never asked for.
    axes: z.array(tuningAxisSchema).min(1).optional(),
    // Whether this slot may drop a matched record from the quest's scenario
    // entirely. Off by default: turning a record off is a bigger claim than nudging
    // its value, so a template opts into it deliberately.
    canMute: z.boolean().default(false),
  })
  // A slot that neither adjusts an axis nor mutes can only render an empty row.
  // Cheaper to reject at parse time than to wonder later why a tuning appears and
  // does nothing.
  .refine(tuning => tuning.axes !== undefined || tuning.canMute, {
    error: 'a tuning must declare at least one axis or set canMute',
  })

export type Tuning = z.output<typeof tuningSchema>

export default tuningSchema
